import * as React from 'react';
import styled from 'styled-components';

import { Form } from '@longline/aqua-ui/containers/Form';
import { Dropdown } from '@longline/aqua-ui/inputs/Dropdown';
import { Slider } from '@longline/aqua-ui/inputs/Slider';

import { IArea, ICultureStartMonth, CULTURE_START_MONTHS } from '../../../types/IArea';
import { useGlobalState } from '../../../contexts/state/GlobalStateContext';
import { IDriverset, stripDateIndices } from '../../../api/models';
import { isShellfishModel } from '../../../api/models/ISpecies';
import { speciesModelCodeFor, speciesDefaultsFor } from '../../../types/IProject';
import { AquaFishChart } from '../../../chart/AquaFishChart';
import { AquaShellChart } from '../../../chart/AquaShellChart';
import { Dialog } from '@longline/aqua-ui/services/Dialog';
import { DriversetViewer } from '../../../ui/DriversetViewer.tsx';
import { Pane } from '../../../ui/Pane';
import { LinkButton } from '../../../ui/LinkButton';
import { Can } from '../../../ui/Can';
import { DriversetEditDialog } from '../../../ui/DriversetEditDialog';
import { useConfirm } from '../../../contexts/confirm/ConfirmContext';

// Sentinel entry shown in the dropdown when the area's driver set has been
// hand-edited (custom: true). Module-scoped so its identity is stable across
// renders (the dropdown matches `value` to entries in `data` by reference).
const CUSTOM_ENTRY: IDriverset = { name: "Custom", dates: [], custom: true };

interface IProps {
  value?: IArea;
}

const DriverTab = (props: IProps) => {
  const globalState = useGlobalState();
  const confirm = useConfirm();

  const [viewing, setViewing] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  // Bumped to force-remount the dropdown when we need to undo an optimistic
  // visual selection — the dropdown commits the user's click before our
  // onChange runs, so the displayed value can drift from the model.
  const [dropdownKey, setDropdownKey] = React.useState(0);

  // Optional because the parent <Tabs> may render before an area is selected.
  if (!props.value) return null;
  const area = props.value;

  const handleUpdate = (field: string, value: any) => {
    (area as any)[field] = value;
    globalState.updateArea(area);
  }

  // The dropdown shows system driver sets, plus the synthetic Custom entry
  // when the area's snapshot has been hand-edited. For non-custom snapshots,
  // we resolve which system entry it came from by name (the only stable
  // identifier left after Laravel hides uuids).
  const isCustom = area.driverset?.custom === true;
  const matchedSystem = !isCustom && area.driverset
    ? globalState.driversets.find(ds => ds.name === area.driverset?.name) ?? null
    : null;

  const dropdownData = isCustom
    ? [CUSTOM_ENTRY, ...globalState.driversets]
    : globalState.driversets;
  const dropdownValue: IDriverset | null = isCustom ? CUSTOM_ENTRY : matchedSystem;

  // Picking a system driver set destructively replaces the area's snapshot.
  // If the snapshot is currently custom, confirm first — the custom edits
  // would be lost. Re-selecting Custom is a no-op.
  const handleSelect = (selected: IDriverset | null) => {
    if (selected === CUSTOM_ENTRY) return;
    const replace = () => {
      area.driverset = selected
        ? { name: selected.name, dates: selected.dates, custom: false }
        : null;
      globalState.updateArea(area);
    }
    if (isCustom) {
      // Snap the dropdown back to the model state. If the user confirms,
      // replace() will update the model and re-drive the dropdown.
      setDropdownKey(k => k + 1);
      confirm.start(
        replace,
        "Discard custom driver set?",
        "Loading a system driver set will discard your custom changes. Proceed?"
      );
    } else {
      replace();
    }
  }

  const handleSaveEdit = (edited: IDriverset) => {
    // After an admin edit, the snapshot is no longer a system driver set.
    // Strip the editor's transient row indices so they don't persist.
    area.driverset = { ...stripDateIndices(edited), name: "Custom", custom: true };
    globalState.updateArea(area);
    setEditing(false);
  }

  const editorSeed: IDriverset = area.driverset ?? { name: "Custom", dates: [], custom: true };
  const headerName = isCustom ? "Custom" : area.driverset?.name;

  // The dropdown matches `value` to entries in `data` by reference, so resolve
  // the selection to the module-scoped table entry. An unset startMonth is
  // January — that is what every area assumed before the field existed.
  const startMonth = CULTURE_START_MONTHS.find(m => m.month === (area.startMonth ?? 1)) ?? null;

  const speciesCode = speciesModelCodeFor(globalState.project);
  const speciesDefaults = speciesDefaultsFor(globalState.project, globalState.species);

  const noSystemDriversets = globalState.driversets.length === 0;

  return (
    <>
      {noSystemDriversets ? (
        <NoDriversets>There are no system driver sets.</NoDriversets>
      ) : (
        <Form.Field
          key={dropdownKey}
          name=""
          label="Driver set"
          wrapper="box"
          value={dropdownValue}
          onChange={handleSelect}
          control={<Dropdown
            fluid
            data={dropdownData}
            placeholder="Select a driver set"
            label={(ds: IDriverset) => ds.name}
          >
            <Dropdown.Column>{(item: IDriverset) => item.name}</Dropdown.Column>
          </Dropdown>}
          hint={<>Environmental parameters for growth.</>}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'end', gap: '8px', paddingBottom: '8px'}}>
        <LinkButton disabled={area.driverset == null} onClick={() => setViewing(true)}>View driver set</LinkButton>
        <Can permission="edit_driverset">
          <LinkButton onClick={() => setEditing(true)}>Edit driver set</LinkButton>
        </Can>
      </div>
      <Form.Field
        name=""
        label="Culture days"
        wrapper="box"
        value={area.days}
        onChange={v => handleUpdate("days", v)}
        control={<Slider padded min={10} max={1510} fluid snapToTicks ticks={151} hideTicks color="white"/>}
        hint="Length of the culture period."
      />

      <Form.Field
        name=""
        label="Culture start"
        wrapper="box"
        value={startMonth}
        onChange={(m: ICultureStartMonth | null) => handleUpdate("startMonth", m ? m.month : null)}
        control={<Dropdown
          fluid
          data={CULTURE_START_MONTHS}
          placeholder="Select a start month"
          label={(m: ICultureStartMonth) => `${m.name} - day ${m.day + 1}`}
        >
          <Dropdown.Column>{(m: ICultureStartMonth) => m.name}</Dropdown.Column>
          {/* day is stored 0-based; shown 1-based, so January reads "day 1". */}
          <Dropdown.Column width="72px" align="right">{(m: ICultureStartMonth) => <DayHint>day {m.day + 1}</DayHint>}</Dropdown.Column>
        </Dropdown>}
        hint="Month the culture period starts."
      />

      {/* AquaFish only. A shellfish project would fall through AquaFish's factory
          to salmon, so show nothing rather than a curve for the wrong animal. */}
      {/* Shellfish and finfish have different growth models and different
          outputs, so the project's species decides which chart is shown. */}
      {isShellfishModel(speciesCode)
        ? <AquaShellChart days={area.days} startMonth={area.startMonth} species={speciesCode}
                          initialWeight={speciesDefaults.initialWeight ?? undefined} driverset={area.driverset}/>
        : <AquaFishChart  days={area.days} startMonth={area.startMonth} species={speciesCode}
                          initialWeight={speciesDefaults.initialWeight ?? undefined} driverset={area.driverset}/>}

      <Dialog width={1200} inverted open={viewing} onClose={() => setViewing(false)}>
        <Dialog.Header>
          Driver set: {headerName}
        </Dialog.Header>
        <Dialog.Content nopadding>
          <div style={{ height: '300px' }}>
            <DriversetViewer driverset={area.driverset}/>
          </div>
        </Dialog.Content>
        <Dialog.Footer align="end">
          <Pane.CancelButton onClick={() => setViewing(false)}>CLOSE</Pane.CancelButton>
        </Dialog.Footer>
      </Dialog>

      {editing && <DriversetEditDialog
        driverset={editorSeed}
        onClose={() => setEditing(false)}
        onSave={handleSaveEdit}
      />}
    </>
  )
}

const DayHint = styled.span`
  font-size: 80%;
  color: ${p => p.theme.colors.neutral[50]};
`

const NoDriversets = styled.div`
  padding: 12px 16px;
  font-style: italic;
  color: #fff;
`

export { DriverTab }
